import React, { useState, useEffect } from 'react';
import {
  Table,
  Form,
  Input,
  Select,
  Button,
  Card,
  Col,
  message,
  DatePicker,
  Tag,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../../components/common/FilterPanel';
import * as reportService from '../../../services/report/reportService';

const { RangePicker } = DatePicker;

// 支付类型选项
const PAYMENT_TYPE_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'O2O Instant CP', label: 'O2O Instant CP' },
  { value: 'O2O Credit Online', label: 'O2O Credit Online' },
  { value: 'O2O TMN at Door', label: 'O2O TMN at Door' },
  { value: 'O2O Voucher Online', label: 'O2O Voucher Online' },
  { value: 'O2O Member CP', label: 'O2O Member CP' },
];

/**
 * 支付发票报表
 * 路由: /report/payment-invoice
 */
const PaymentInvoiceReport = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`,
  });

  const columns = [
    {
      title: 'Till Date',
      dataIndex: 'tillDate',
      key: 'tillDate',
      fixed: 'left',
      width: 120,
    },
    {
      title: 'Store No',
      dataIndex: 'storeNo',
      key: 'storeNo',
      width: 100,
    },
    {
      title: 'Store Name',
      dataIndex: 'storeName',
      key: 'storeName',
      width: 200,
    },
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 200,
    },
    {
      title: 'Payment Type',
      dataIndex: 'paymentType',
      key: 'paymentType',
      width: 160,
    },
    {
      title: 'Amount (THB)',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v) => v != null ? `฿${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
    },
    {
      title: 'POS No',
      dataIndex: 'posNo',
      key: 'posNo',
      width: 100,
      align: 'center',
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel) => (
        <Tag color={channel === 'Lotus' ? 'blue' : 'purple'}>{channel}</Tag>
      ),
    },
  ];

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const formValues = form.getFieldsValue();
      const searchParams = {
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...params,
      };

      if (formValues.tillDate && formValues.tillDate.length === 2) {
        searchParams.tillDateStart = formValues.tillDate[0].format('YYYY-MM-DD');
        searchParams.tillDateEnd = formValues.tillDate[1].format('YYYY-MM-DD');
      }
      if (formValues.storeNo) searchParams.storeNo = formValues.storeNo;
      if (formValues.storeName) searchParams.storeName = formValues.storeName;
      if (formValues.paymentType) searchParams.paymentType = formValues.paymentType;
      if (formValues.orderNumber) searchParams.orderNumber = formValues.orderNumber;

      const response = await reportService.pagePaymentInvoice(searchParams);
      if (response.success) {
        const data = response.data || {};
        setDataSource(data.tableData || []);
        setPagination((prev) => ({
          ...prev,
          current: data.paging?.currentPage || 1,
          total: data.paging?.totalCount || 0,
        }));
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Query payment invoice report failed:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleTableChange = (paginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
      >
        <Col span={8}>
          <Form.Item name="tillDate" label="Till Date">
            <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item name="storeNo" label="Store No">
            <Input placeholder="Please enter" />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item name="storeName" label="Store Name">
            <Input placeholder="Please enter" />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item name="paymentType" label="Payment Type">
            <Select placeholder="Please select" options={PAYMENT_TYPE_OPTIONS} allowClear />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item name="orderNumber" label="Order Number">
            <Input placeholder="Please enter" />
          </Form.Item>
        </Col>
      </FilterPanel>

      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button icon={<ExportOutlined />}>Export</Button>
      </div>

      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0 }}>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="orderNumber"
          scroll={{ x: 1300, y: 'calc(100vh - 420px)' }}
        />
      </Card>
    </div>
  );
};

export default PaymentInvoiceReport;
